import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import Alert from '@mui/joy/Alert';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Grid from '@mui/joy/Grid';
import Input from '@mui/joy/Input';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Card from '@mui/joy/Card';
import Stack from '@mui/joy/Stack';

// Icons
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import SortIcon from '@mui/icons-material/Sort';
import WorkspacesIcon from '@mui/icons-material/Workspaces';

// Services
import { useAuth } from '../contexts/AuthContext';
import { projectService, workspaceService } from '../api/services';

// Custom Components
import ModernSidebar from '../components/layout/ModernSidebar';
import ModernHeader from '../components/layout/ModernHeader';
import ModernProjectCard from '../components/modern/ModernProjectCard';
import ModernCreateWorkspaceModal from '../components/modals/ModernCreateWorkspaceModal';
import ModernCreateProjectModal from '../components/modals/ModernCreateProjectModal';
import WorkspacePromptEditor from './modals/WorkspacePromptEditor';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import LoadingState from '../components/states/LoadingState';
import AssistantChat from '../components/modern/AssistantChat';

import { themeColors, shadows } from '../styles/theme';

export default function ModernCreatorDashboard() {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();

  // Data States
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [projects, setProjects] = useState([]);

  // UI States
  const [loading, setLoading] = useState(true);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [viewTransitioning, setViewTransitioning] = useState(false);

  // Search and Filter
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('recent');

  // Modal States
  const [createWorkspaceOpen, setCreateWorkspaceOpen] = useState(false);
  const [createProjectOpen, setCreateProjectOpen] = useState(false);
  const [workspacePromptOpen, setWorkspacePromptOpen] = useState(false);
  const [deleteProjectOpen, setDeleteProjectOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  // Load Workspaces on Mount
  useEffect(() => {
    if (isAuthenticated) {
      fetchWorkspaces();
    }
  }, [isAuthenticated]);

  // Fetch Workspaces
  const fetchWorkspaces = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await workspaceService.getAllWorkspaces();
      setWorkspaces(data);
    } catch (err) {
      console.error('Error fetching workspaces:', err);
      setError('Failed to load workspaces. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Projects when Workspace Changes
  useEffect(() => {
    if (selectedWorkspace) {
      fetchWorkspaceProjects(selectedWorkspace.id);
    }
  }, [selectedWorkspace]);

  const fetchWorkspaceProjects = async (workspaceId) => {
    try {
      setProjectsLoading(true);
      const data = await workspaceService.getWorkspaceProjects(workspaceId);
      setProjects(data);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError('Failed to load flows. Please try again.');
    } finally {
      setProjectsLoading(false);
    }
  };

  // --- Handlers: Navigation ---
  const handleShowAllWorkspaces = () => {
    setViewTransitioning(true);
    setTimeout(() => {
      setSelectedWorkspace(null);
      setProjects([]);
      setSearchQuery('');
      setTimeout(() => setViewTransitioning(false), 50);
    }, 300);
  };

  const handleWorkspaceSelect = (workspace) => {
    setViewTransitioning(true);
    setTimeout(() => {
      setSelectedWorkspace(workspace);
      setSearchQuery('');
      setTimeout(() => setViewTransitioning(false), 50);
    }, 300);
  };

  // --- Handlers: Workspace ---
  const handleCreateWorkspace = async (workspaceData) => {
    try {
      setError(null);
      const created = await workspaceService.createWorkspace(workspaceData);
      setWorkspaces([created, ...workspaces]);
      setCreateWorkspaceOpen(false);
      setSelectedWorkspace(created);
    } catch (err) {
      console.error('Error creating workspace:', err);
      setError('Failed to create workspace. Please try again.');
      throw err;
    }
  };

  // Save the workspace-level BI prompt template (closing handled by the modal).
  const handleWorkspacePromptSave = async (newPrompt) => {
    if (!selectedWorkspace) return;
    const updated = await workspaceService.updateWorkspace(
      selectedWorkspace.id,
      { bi_agent_prompt: newPrompt }
    );
    setSelectedWorkspace(updated);
    setWorkspaces((prev) => prev.map((w) => (w.id === updated.id ? updated : w)));
  };

  // --- Handlers: Project ---
  const handleCreateProject = async (projectData) => {
    if (!selectedWorkspace) return;

    try {
      setError(null);
      const payload = { ...projectData, workspace_id: selectedWorkspace.id };
      const created = await projectService.createProject(payload);
      setProjects([created, ...projects]);
      setCreateProjectOpen(false);
      navigate(`/flow/${created.id}`);
    } catch (err) {
      console.error('Error creating project:', err);
      setError('Failed to create flow. Please try again.');
      throw err;
    }
  };

  const handleProjectEdit = (project) => {
    navigate(`/flow/${project.id}`);
  };

  const handleProjectDelete = (project) => {
    setSelectedProject(project);
    setDeleteProjectOpen(true);
  };

  const handleDeleteProject = async () => {
    if (!selectedProject) return;

    try {
      await projectService.deleteProject(selectedProject.id);
      setProjects(projects.filter(p => p.id !== selectedProject.id));
      setDeleteProjectOpen(false);
      setSelectedProject(null);
    } catch (err) {
      console.error('Error deleting project:', err);
      setError('Failed to delete flow. Please try again.');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Filter and Sort Projects
  const filteredProjects = projects
    .filter(project =>
      project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.url_name.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'recent':
        default:
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Sheet
      sx={{
        height: '100vh',
        display: 'flex',
        overflow: 'hidden',
        backgroundColor: themeColors.neutral[50],
      }}
    >
      {/* Sidebar */}
      <ModernSidebar
        workspaces={workspaces}
        selectedWorkspace={selectedWorkspace}
        onWorkspaceSelect={handleWorkspaceSelect}
        onCreateWorkspace={() => setCreateWorkspaceOpen(true)}
        onShowAllWorkspaces={handleShowAllWorkspaces}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <ModernHeader
          selectedWorkspace={selectedWorkspace}
          user={user}
          onLogout={handleLogout}
          title="All Workspaces"
        />

        {/* Global Error Alert */}
        {error && (
          <Alert
            color="danger"
            sx={{ m: 3, mb: 0 }}
            variant="soft"
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Content Area */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 4 }}>
          <Box
            sx={{
              opacity: viewTransitioning ? 0 : 1,
              transform: viewTransitioning ? 'translateY(20px)' : 'translateY(0)',
              transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
          {!selectedWorkspace ? (
            /* All Workspaces View */
            loading ? (
              <LoadingState message="Loading workspaces..." />
            ) : workspaces.length === 0 ? (
              <Card
                sx={{
                  maxWidth: 600,
                  mx: 'auto',
                  mt: 8,
                  p: 6,
                  textAlign: 'center',
                  border: `2px dashed ${themeColors.neutral[300]}`,
                  backgroundColor: 'white',
                  boxShadow: shadows.md,
                  borderRadius: 3,
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: themeColors.primary[50],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  <WorkspacesIcon sx={{ fontSize: 40, color: themeColors.primary[500] }} />
                </Box>
                <Typography level="h3" sx={{ mb: 2, fontWeight: 700, color: themeColors.neutral[900] }}>
                  Welcome to Vocative
                </Typography>
                <Typography level="body-lg" sx={{ color: themeColors.neutral[600], mb: 4 }}>
                  Create your first workspace to start building automated flows and managing your projects.
                </Typography>
                <Button
                  size="lg"
                  startDecorator={<AddIcon />}
                  onClick={() => setCreateWorkspaceOpen(true)}
                  sx={{
                    background: `linear-gradient(135deg, ${themeColors.primary[500]}, ${themeColors.primary[700]})`,
                    boxShadow: `0 4px 12px ${themeColors.primary[500]}40`,
                    px: 4,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 6px 16px ${themeColors.primary[500]}50`,
                    },
                  }}
                >
                  Create Your First Workspace
                </Button>
              </Card>
            ) : (
              <Box>
                <Typography level="h2" sx={{ mb: 4, fontWeight: 700, color: themeColors.neutral[900] }}>
                  Your Workspaces
                </Typography>

                <Grid container spacing={3}>
                  {workspaces.map((workspace, index) => (
                    <Grid key={workspace.id} xs={12} sm={6} md={4}>
                      <Card
                        sx={{
                          height: '100%',
                          cursor: 'pointer',
                          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                          backgroundColor: 'white',
                          border: 'none',
                          boxShadow: shadows.md,
                          borderRadius: 3,
                          animation: `fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.1}s both`,
                          '@keyframes fadeInUp': {
                            '0%': {
                              opacity: 0,
                              transform: 'translateY(30px)',
                            },
                            '100%': {
                              opacity: 1,
                              transform: 'translateY(0)',
                            },
                          },
                          '&:hover': {
                            transform: 'translateY(-6px)',
                            boxShadow: shadows.xl,
                          },
                        }}
                        onClick={() => handleWorkspaceSelect(workspace)}
                      >
                        <Box sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            {workspace.logo_url || workspace.logo ? (
                              <Box
                                component="img"
                                src={workspace.logo_url || workspace.logo}
                                alt={workspace.name}
                                sx={{
                                  width: 64,
                                  height: 64,
                                  borderRadius: 2.5,
                                  objectFit: 'cover',
                                  boxShadow: shadows.md,
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: 64,
                                  height: 64,
                                  borderRadius: 2.5,
                                  background: `linear-gradient(135deg, ${workspace.main_color || themeColors.primary[500]}, ${workspace.main_color || themeColors.primary[600]})`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'white',
                                  fontWeight: 700,
                                  fontSize: '1.5rem',
                                  boxShadow: shadows.md,
                                }}
                              >
                                {workspace.name[0]?.toUpperCase()}
                              </Box>
                            )}
                          </Box>

                          <Typography level="title-lg" sx={{ fontWeight: 700, mb: 1, color: themeColors.neutral[900] }}>
                            {workspace.name}
                          </Typography>

                          <Typography level="body-sm" sx={{ color: themeColors.neutral[600], mb: 2, minHeight: '3rem' }}>
                            {workspace.description || 'No description provided'}
                          </Typography>

                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            pt: 2,
                            position: 'relative',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              height: '1px',
                              background: `linear-gradient(90deg, transparent, ${themeColors.neutral[200]}, transparent)`,
                            },
                          }}>
                            <Typography level="body-sm" sx={{ color: themeColors.neutral[700], fontWeight: 600 }}>
                              {workspace.project_count || 0} flows
                            </Typography>
                          </Box>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )
          ) : (
            /* Selected Workspace - Projects View */
            <Box>
              {/* Toolbar */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4, gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                  <Input
                    placeholder="Search flows..."
                    startDecorator={<SearchIcon />}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{
                      minWidth: 300,
                      backgroundColor: 'white',
                      border: 'none',
                      boxShadow: shadows.sm,
                      borderRadius: 2.5,
                      '--Input-focusedThickness': '0px',
                      '&:focus-within': {
                        boxShadow: shadows.focus,
                      },
                      transition: 'all 0.2s ease',
                    }}
                  />

                  <Select
                    value={sortBy}
                    onChange={(_, value) => setSortBy(value)}
                    startDecorator={<SortIcon />}
                    sx={{
                      minWidth: 140,
                      backgroundColor: 'white',
                      border: 'none',
                      boxShadow: shadows.sm,
                      borderRadius: 2.5,
                      '&:hover': {
                        boxShadow: shadows.md,
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <Option value="recent">Recent</Option>
                    <Option value="name">Name</Option>
                  </Select>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Button
                    size="lg"
                    variant="outlined"
                    color="neutral"
                    startDecorator={<AutoAwesomeOutlinedIcon />}
                    onClick={() => setWorkspacePromptOpen(true)}
                    sx={{
                      backgroundColor: 'white',
                      border: 'none',
                      boxShadow: shadows.sm,
                      fontWeight: 600,
                      px: 3,
                      color: themeColors.neutral[700],
                      '&:hover': {
                        backgroundColor: 'white',
                        boxShadow: shadows.md,
                        transform: 'translateY(-1px)',
                      },
                    }}
                  >
                    BI Prompt
                  </Button>

                  <Button
                    size="lg"
                    startDecorator={<AddIcon />}
                    onClick={() => setCreateProjectOpen(true)}
                    sx={{
                      background: `linear-gradient(135deg, ${themeColors.primary[500]}, ${themeColors.primary[700]})`,
                      boxShadow: `0 4px 12px ${themeColors.primary[500]}40`,
                      fontWeight: 600,
                      px: 3,
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 6px 16px ${themeColors.primary[500]}50`,
                      },
                    }}
                  >
                    New Flow
                  </Button>
                </Box>
              </Box>

              {/* Projects Grid */}
              {projectsLoading ? (
                <LoadingState message="Loading flows..." />
              ) : filteredProjects.length === 0 ? (
                <Card
                  sx={{
                    p: 8,
                    textAlign: 'center',
                    border: `2px dashed ${themeColors.neutral[300]}`,
                    backgroundColor: 'white',
                    boxShadow: shadows.md,
                    borderRadius: 3,
                  }}
                >
                  {searchQuery ? (
                    <>
                      <SearchIcon sx={{ fontSize: 48, color: themeColors.neutral[400], mb: 2 }} />
                      <Typography level="title-lg" sx={{ mb: 1, fontWeight: 600 }}>
                        No flows found
                      </Typography>
                      <Typography level="body-md" sx={{ color: themeColors.neutral[600], mb: 3 }}>
                        No flows match "{searchQuery}"
                      </Typography>
                      <Button variant="outlined" onClick={() => setSearchQuery('')}>
                        Clear Search
                      </Button>
                    </>
                  ) : (
                    <>
                      <Typography level="h3" sx={{ mb: 2, fontWeight: 700 }}>
                        Create your first flow
                      </Typography>
                      <Typography level="body-lg" sx={{ color: themeColors.neutral[600], mb: 4, maxWidth: 480, mx: 'auto' }}>
                        Flows are automated workflows that process data, generate documents, and more.
                      </Typography>
                      <Button
                        size="lg"
                        startDecorator={<AddIcon />}
                        onClick={() => setCreateProjectOpen(true)}
                        sx={{
                          background: `linear-gradient(135deg, ${themeColors.primary[500]}, ${themeColors.primary[700]})`,
                          boxShadow: `0 4px 12px ${themeColors.primary[500]}40`,
                        }}
                      >
                        Create Your First Flow
                      </Button>
                    </>
                  )}
                </Card>
              ) : (
                <Grid container spacing={3}>
                  {filteredProjects.map((project, index) => (
                    <Grid
                      key={project.id}
                      xs={12}
                      sm={6}
                      md={4}
                      lg={3}
                      sx={{
                        animation: `fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.08}s both`,
                        '@keyframes fadeInUp': {
                          '0%': {
                            opacity: 0,
                            transform: 'translateY(30px)',
                          },
                          '100%': {
                            opacity: 1,
                            transform: 'translateY(0)',
                          },
                        },
                      }}
                    >
                      <ModernProjectCard
                        project={project}
                        workspace={selectedWorkspace}
                        onEdit={handleProjectEdit}
                        onDelete={handleProjectDelete}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}
          </Box>
        </Box>
      </Box>

      {/* Modals */}
      <WorkspacePromptEditor
        open={workspacePromptOpen}
        onClose={() => setWorkspacePromptOpen(false)}
        value={selectedWorkspace?.bi_agent_prompt || ''}
        onSave={handleWorkspacePromptSave}
        availableVariables={['project_contexts', ...projects.map((p) => p.name)]}
      />

      <ModernCreateWorkspaceModal
        open={createWorkspaceOpen}
        onClose={() => setCreateWorkspaceOpen(false)}
        onSubmit={handleCreateWorkspace}
        loading={loading}
        error={error}
      />

      <ModernCreateProjectModal
        open={createProjectOpen}
        onClose={() => setCreateProjectOpen(false)}
        onSubmit={handleCreateProject}
        loading={loading}
        error={error}
        workspace={selectedWorkspace}
      />

      {/* Delete Project Modal */}
      <Modal open={deleteProjectOpen} onClose={() => setDeleteProjectOpen(false)}>
        <ModalDialog>
          <Typography level="h4" sx={{ mb: 2, fontWeight: 700 }}>
            Delete Flow
          </Typography>
          <Typography level="body-md" sx={{ mb: 3, color: themeColors.neutral[700] }}>
            Are you sure you want to delete "{selectedProject?.name}"?
            This action cannot be undone.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={() => setDeleteProjectOpen(false)}>
              Cancel
            </Button>
            <Button
              color="danger"
              onClick={handleDeleteProject}
              sx={{
                backgroundColor: themeColors.danger,
                '&:hover': {
                  backgroundColor: `${themeColors.danger}DD`,
                },
              }}
            >
              Delete Flow
            </Button>
          </Box>
        </ModalDialog>
      </Modal>

      <AssistantChat />
    </Sheet>
  );
}
